import {
  useAssignShiftToTeam,
  useRemoveTeamShift,
  useShifts,
  useTeamShifts,
} from '@/domains/shifts';
import { TeamDetailView } from '@/domains/teams/components/TeamDetailView';
import { UtilisationPeriodSelector } from '@/domains/teams/components/UtilisationPeriodSelector';
import {
  useAddTeamMember,
  useDeleteTeam,
  useRemoveTeamMember,
  useTeam,
  useUpdateTeam,
} from '@/domains/teams/hooks/useTeams';
import { useUtilisationPeriod } from '@/domains/teams/hooks/useUtilisationPeriod';
import { formatPeriodLabel } from '@/domains/teams/lib/utilisationPeriod';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';

export function TeamDetailPage() {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();

  const id = teamId ? Number(teamId) : null;
  // Utilisation is period-scoped (CWL-157) — the selected range is refetched.
  const { period, range, query, selectorProps } = useUtilisationPeriod();
  const { data: team, isLoading } = useTeam(id, query);
  const addMember = useAddTeamMember(id ?? 0);
  const removeMember = useRemoveTeamMember(id ?? 0);
  const updateTeam = useUpdateTeam(id ?? 0);
  const deleteTeam = useDeleteTeam();
  const { data: shiftsData } = useShifts();
  const { data: teamShiftsData } = useTeamShifts(id);
  const assignShiftToTeam = useAssignShiftToTeam();
  const removeTeamShift = useRemoveTeamShift();

  const allShifts = useMemo(() => shiftsData?.values ?? [], [shiftsData]);
  const assignedShifts = useMemo(() => teamShiftsData ?? [], [teamShiftsData]);

  if (!teamId) return null;

  return (
    <TeamDetailView
      team={team ?? null}
      isLoading={isLoading}
      onBack={() => navigate('/teams')}
      allShifts={allShifts}
      assignedShifts={assignedShifts}
      period={period}
      utilisationPeriodLabel={formatPeriodLabel(period, range)}
      utilisationControl={<UtilisationPeriodSelector {...selectorProps} showLabel={false} />}
      onAddMembers={async (userIds) => {
        for (const userId of userIds) {
          await addMember.mutateAsync({ userId });
        }
      }}
      onRemoveMember={async (userId, newTeamLeadId) => {
        await removeMember.mutateAsync({ userId, newTeamLeadId });
      }}
      onUpdateTeam={async (data) => {
        await updateTeam.mutateAsync(data);
      }}
      onAssignShift={async (shiftId, startDate, endDate) => {
        await assignShiftToTeam.mutateAsync({
          shiftId,
          teamId: id!,
          startDate,
          endDate,
        });
      }}
      onRemoveShift={async (shiftId, startDate, endDate) => {
        await removeTeamShift.mutateAsync({
          shiftId,
          teamId: id!,
          startDate,
          endDate,
        });
      }}
      onDeleteTeam={async () => {
        await deleteTeam.mutateAsync(id!);
        navigate('/teams');
      }}
    />
  );
}
